import React, { useMemo } from 'react';
import { RevenueTrendPanel } from '../../components/dashboard/RevenueTrendPanel';
import { RevenueMixPanel } from '../../components/dashboard/RevenueMixPanel';
import { StatCard } from '../../components/dashboard/StatCard';
import { ErrorBanner } from '../../components/dashboard/DashboardShared';
import { DashboardInsightChip } from '../../components/dashboard/DashboardInsightChip';
import { NavigablePanel } from '../../components/dashboard/NavigablePanel';
import ErrorBoundary from '../../components/ErrorBoundary';
import { APP_NAV, useAppNavigation } from '../../context/AppNavigationContext';
import { SkeletonCard, SkeletonChart } from '../../components/ui/SkeletonBlock';
import { buildOverviewInsight } from '../../lib/dashboardInsights';

export function OverviewPage({
  committedFilters,
  data,
  panelLoading,
  error,
  currencyCode,
  isFirstLoad,
  stats,
  topEventRows,
  filterSlot = null,
}) {
  const { setActiveNav } = useAppNavigation();
  const queryKey = committedFilters.queryString || '';

  const insight = useMemo(
    () =>
      buildOverviewInsight({
        stats,
        products: data?.revenueByProduct || [],
        topEvents: topEventRows,
      }),
    [stats, data?.revenueByProduct, topEventRows]
  );

  function goToPage(navId) {
    setActiveNav(navId);
  }

  return (
    <section className="flex w-full min-w-0 flex-col gap-5">
      <div className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {isFirstLoad ? (
          <>
            <SkeletonCard featured />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : error ? (
          <ErrorBanner message={error} />
        ) : stats.length === 0 ? (
          <div className="col-span-full rounded-xl border border-slate-200/90 bg-white px-4 py-6 text-center text-sm text-slate-400 shadow-[0_4px_16px_rgba(15,23,42,0.08)]">
            No invoice data found for this period. Try a different date range.
          </div>
        ) : (
          stats.map((stat) => (
            <StatCard key={stat.title} {...stat} />
          ))
        )}
      </div>

      {filterSlot}

      {!isFirstLoad && !error && stats.length > 0 ? (
        <DashboardInsightChip tone={insight.tone} title={insight.title} detail={insight.detail} />
      ) : null}

      {isFirstLoad ? (
        <div className="grid min-w-0 items-stretch gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2"><SkeletonChart height="h-[360px]" /></div>
          <SkeletonChart height="h-[360px]" />
        </div>
      ) : (
        <div className="grid min-w-0 items-stretch gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2 flex min-w-0">
            <NavigablePanel navId={APP_NAV.TRENDS} onNavigate={goToPage} className="min-h-[360px] flex-1">
              <ErrorBoundary key={`rev-overview-${queryKey}`}>
                <RevenueTrendPanel
                  revenueByDate={data?.revenueByDate || []}
                  loading={panelLoading}
                  error={error}
                  variant="detailed"
                  currencyCode={currencyCode}
                />
              </ErrorBoundary>
            </NavigablePanel>
          </div>
          <div className="flex min-w-0">
            <NavigablePanel navId={APP_NAV.PRODUCTS} onNavigate={goToPage} className="min-h-[360px] flex-1">
              <ErrorBoundary key={`mix-overview-${queryKey}`}>
                <RevenueMixPanel
                  revenueByProduct={data?.revenueByProduct || []}
                  loading={panelLoading}
                  error={error}
                  variant="compact"
                  currencyCode={currencyCode}
                />
              </ErrorBoundary>
            </NavigablePanel>
          </div>
        </div>
      )}
    </section>
  );
}
